import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, Paperclip, X, Download } from "lucide-react";
import { LEGAL_NOTE } from "@/components/landing/legalNote";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { nombre: "", telefono: "", email: "", comunidad: "", tipo: "", descripcion: "" };

const inputCls =
  "w-full bg-white border border-slate-200 rounded-lg px-4 py-3.5 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-900 transition-colors duration-300";

export default function Incidencia() {
  const [form, setForm] = useState(empty);
  const [foto, setFoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [referencia, setReferencia] = useState("");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("La fotografía supera los 10 MB. Redúcela e inténtalo de nuevo.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFoto({ name: file.name, data: reader.result });
    reader.readAsDataURL(file);
  };

  const removeFoto = () => {
    setFoto(null);
    const input = document.getElementById("foto-incidencia");
    if (input) input.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/incident`, {
        ...form,
        foto_base64: foto?.data || "",
        foto_nombre: foto?.name || "",
      });
      setReferencia(data.referencia || "");
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "No se pudo registrar la incidencia. Inténtelo de nuevo en unos minutos.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSending(false);
    }
  };

  const downloadReceipt = () => {
    if (!referencia) return;
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const left = 20;
    let y = 25;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Justificante de Incidencia", left, y);
    y += 12;
    doc.setFontSize(14);
    doc.text(`Referencia: #${referencia}`, left, y);
    y += 12;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    const rows = [
      ["Nombre", form.nombre],
      ["Teléfono", form.telefono],
      ["Email", form.email],
      ["Comunidad", form.comunidad],
      ["Tipo de incidencia", form.tipo],
      ["Fecha", new Date().toLocaleString("es-ES")],
    ];
    rows.forEach(([label, value]) => {
      const lines = doc.splitTextToSize(`${label}: ${value || "No indicado"}`, 170);
      doc.text(lines, left, y);
      y += lines.length * 6;
    });

    y += 4;
    doc.setFontSize(9);
    doc.text(
      doc.splitTextToSize(
        "Conserve este justificante para cualquier consulta. Las imágenes adjuntas se eliminan automáticamente a los 45 días conforme a la política de protección de datos.",
        170,
      ),
      left,
      y,
    );
    doc.save(`justificante-${referencia}.pdf`);
  };

  return (
    <div data-testid="incidencia-page" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="incidencia-brand" className="flex items-center">
            <img src="/logo.png" alt="AFGestiones — Gestión y Administración de Fincas" className="h-12 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="incidencia-back-link"
            onClick={() => {
              document.body.style.overflow = "auto";
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        {done ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="incidencia-success">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
            <h1 className="font-display font-bold tracking-tight text-slate-900 text-2xl sm:text-3xl mt-6">
              Incidencia registrada
            </h1>
            <p className="mt-4 text-slate-500 leading-relaxed">
              Su aviso ha quedado documentado con trazabilidad. Nuestro equipo técnico lo revisará y le contactará a la mayor brevedad.
            </p>
            {referencia && (
              <div className="mt-6 border border-slate-200 bg-slate-50 rounded-xl px-5 py-4" data-testid="incidencia-reference">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">Referencia del aviso</p>
                <p className="mt-2 font-display text-2xl font-bold text-slate-900" data-testid="incidencia-reference-code">#{referencia}</p>
                <p className="mt-2 text-xs text-slate-500">Guarde esta referencia para cualquier consulta.</p>
              </div>
            )}
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              {referencia && (
                <button
                  type="button"
                  onClick={downloadReceipt}
                  data-testid="incidencia-download-receipt"
                  className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-slate-900 text-slate-900 font-semibold text-sm rounded-full px-8 py-3.5 transition-colors duration-300"
                >
                  <Download className="w-4 h-4" /> Descargar Justificante PDF
                </button>
              )}
              <Link
                to="/"
                data-testid="incidencia-success-home"
                onClick={() => {
                  document.body.style.overflow = "auto";
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm rounded-full px-8 py-3.5 transition-colors duration-300"
              >
                Volver al inicio
              </Link>
            </div>
          </div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-slate-400 mb-4">Comunidades administradas</p>
            <h1 className="font-display section-title text-slate-900" data-testid="incidencia-title">
              Notificar Incidencia
            </h1>
            <p className="section-subtitle">
              Registro oficial de averías. Su aviso queda documentado y llega al instante al despacho.
            </p>

            <form onSubmit={handleSubmit} data-testid="incidencia-form" className="mt-10 bg-white border border-slate-200 rounded-2xl p-7 sm:p-9 space-y-5">
              {error && (
                <div data-testid="incidencia-error" className="border border-red-200 bg-red-50 text-red-800 text-sm leading-relaxed rounded-lg p-4">
                  {error}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="inc-nombre" className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2.5">Nombre y apellidos</label>
                  <input id="inc-nombre" data-testid="incidencia-input-nombre" required placeholder="Ej. María Betancor" value={form.nombre} onChange={set("nombre")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="inc-telefono" className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2.5">Teléfono</label>
                  <input id="inc-telefono" type="tel" data-testid="incidencia-input-telefono" required placeholder="Ej. 600 123 456" value={form.telefono} onChange={set("telefono")} className={inputCls} />
                </div>
              </div>
              <div>
                <label htmlFor="inc-email" className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2.5">Correo electrónico</label>
                <input id="inc-email" type="email" data-testid="incidencia-input-email" required placeholder="maria@ejemplo.com" value={form.email} onChange={set("email")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="inc-comunidad" className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2.5">Comunidad o dirección del edificio</label>
                <input id="inc-comunidad" type="text" data-testid="incidencia-input-comunidad" required placeholder="Ej: Calle o nombre del edificio..." value={form.comunidad} onChange={set("comunidad")} className={inputCls} autoComplete="off" />
              </div>
              <div>
                <label htmlFor="tipo-incidencia" className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2.5">Tipo de incidencia / avería</label>
                <select
                  id="tipo-incidencia"
                  name="tipo-incidencia"
                  required
                  data-testid="incidencia-input-tipo"
                  value={form.tipo}
                  onChange={set("tipo")}
                  className={`${inputCls} ${form.tipo ? "" : "text-slate-400"}`}
                >
                  <option value="" disabled>Selecciona la tipología de la avería...</option>
                  <option value="Puerta de garaje / Accesos">Puerta de garaje / Accesos</option>
                  <option value="Humedades / Fontanería">Humedades / Fontanería</option>
                  <option value="Electricidad / Iluminación">Electricidad / Iluminación</option>
                  <option value="Ascensor">Ascensor</option>
                  <option value="Cerrajería / Puertas peatonales">Cerrajería / Puertas peatonales</option>
                  <option value="Limpieza y zonas comunes">Limpieza y zonas comunes</option>
                  <option value="Otra incidencia">Otra incidencia (especificar en detalle)</option>
                </select>
              </div>
              <div>
                <label htmlFor="inc-descripcion" className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2.5">Descripción de la avería</label>
                <textarea id="inc-descripcion" rows={4} data-testid="incidencia-input-descripcion" required placeholder="Describa la incidencia con el mayor detalle posible..." value={form.descripcion} onChange={set("descripcion")} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <span className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2.5">
                  Fotografía de la incidencia (opcional)
                </span>
                <label
                  htmlFor="foto-incidencia"
                  data-testid="incidencia-foto-label"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 border border-slate-300 rounded-full px-5 py-2.5 cursor-pointer hover:border-slate-900 transition-colors duration-300"
                >
                  <Paperclip className="w-4 h-4" /> Adjuntar fotografía de la incidencia
                </label>
                <input
                  id="foto-incidencia"
                  name="foto"
                  type="file"
                  accept="image/*"
                  data-testid="incidencia-foto-input"
                  onChange={handleFile}
                  className="hidden"
                />
                <p className="mt-2 text-xs text-slate-400">Formatos admitidos: JPG, PNG o HEIC (máx. 10 MB)</p>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500" data-testid="incidencia-foto-retention-note">
                  Las imágenes adjuntas se almacenan de forma segura y se eliminan automáticamente a los 45 días conforme a nuestra política de protección de datos.
                </p>
                {foto && (
                  <div className="mt-3 inline-flex items-center gap-3 border border-slate-200 rounded-lg p-2.5 bg-slate-50" data-testid="incidencia-foto-preview">
                    <img src={foto.data} alt="Vista previa de la fotografía" className="w-12 h-12 rounded object-cover" />
                    <span className="text-xs text-slate-600 max-w-[160px] truncate">{foto.name}</span>
                    <button
                      type="button"
                      onClick={removeFoto}
                      data-testid="incidencia-foto-remove"
                      className="p-1 text-slate-400 hover:text-slate-900 transition-colors duration-300"
                      aria-label="Quitar fotografía"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="incidencia-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold py-4 rounded-lg transition-colors duration-300"
              >
                {sending ? (<>Registrando <Loader2 className="w-4 h-4 animate-spin" /></>) : (<>Registrar Incidencia <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <label className="flex items-start gap-3 text-xs text-slate-500 leading-relaxed cursor-pointer" data-testid="incidencia-privacidad-label">
                <input type="checkbox" required data-testid="incidencia-input-privacidad" className="mt-0.5 accent-slate-900" />
                <span>
                  He leído y acepto la{" "}
                  <Link to="/politica-de-privacidad" className="underline underline-offset-2 hover:text-slate-900 transition-colors duration-300">
                    Política de Privacidad
                  </Link>
                  .
                </span>
              </label>
              <p data-testid="incidencia-legal-note" className="text-[11px] leading-relaxed text-slate-400">{LEGAL_NOTE}</p>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
